import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Camera, StopCircle, Keyboard, Loader2, CheckCircle2, Barcode, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { discogsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SearchResults from './SearchResults';

// Haptic feedback helper - with fallback
const vibrate = (pattern) => {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  } catch (e) {
    // Ignore vibration errors on unsupported devices
  }
};

// Vibration patterns
const VIBRATION = {
  success: [100, 50, 100],  // Short-pause-short (success feel)
  scan: [50],               // Quick pulse when barcode detected
  error: [200, 100, 200],   // Long-pause-long (error feel)
};

export default function BarcodeScanner({ onSuccess }) {
  const { user, refreshUser } = useAuth();
  const [mode, setMode] = useState('camera'); // 'camera' or 'manual'
  const [scanning, setScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  
  // New states for improved UX
  const [scannedBarcode, setScannedBarcode] = useState(null); // Shows scanned barcode immediately
  const [scanLocked, setScanLocked] = useState(false); // Prevents multiple scans
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const lastScanRef = useRef(null); // Track last scanned barcode
  
  // Get barcode scan info
  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';
  const barcodeScans = user?.usage?.barcode_scans;
  const scansRemaining = barcodeScans?.remaining ?? 100;
  const scansLimit = barcodeScans?.limit ?? 100;
  const isUnlimited = scansLimit === -1;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (readerRef.current) {
      readerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      setScannedBarcode(null);
      setScanLocked(false);
      lastScanRef.current = null;
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);

      // Import and initialize barcode reader
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      reader.decodeFromVideoDevice(undefined, videoRef.current, async (result) => {
        if (result && !scanLocked) {
          const barcode = result.getText();
          
          // Prevent duplicate scans of same barcode
          if (lastScanRef.current === barcode) {
            return;
          }
          
          // Lock scanning immediately
          lastScanRef.current = barcode;
          setScanLocked(true);
          
          // Haptic feedback - barcode detected!
          vibrate(VIBRATION.scan);
          
          // Show barcode immediately (before Discogs lookup)
          setScannedBarcode(barcode);
          
          // Stop camera
          stopCamera();
          
          // Now search Discogs
          await searchByBarcode(barcode);
        }
      });

    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Could not access camera. Use manual entry.');
      setMode('manual');
    }
  };

  const searchByBarcode = async (barcode) => {
    setLoading(true);
    setResults([]);
    
    try {
      console.log('[BarcodeScanner] Searching for barcode:', barcode);
      const res = await discogsAPI.search({ barcode });
      console.log('[BarcodeScanner] Response:', res.data);
      
      // Refresh user to update scan count
      await refreshUser();
      
      if (res.data.results?.length > 0) {
        // Haptic feedback - success!
        vibrate(VIBRATION.success);
        setResults(res.data.results);
        setScannedBarcode(null); // Clear scanned state, show results
        toast.success(`Found ${res.data.results.length} results`);
      } else {
        // Haptic feedback - no results
        vibrate(VIBRATION.error);
        toast.error('No albums found with this barcode');
        setScannedBarcode(null);
        setScanLocked(false);
      }
    } catch (err) {
      // Haptic feedback - error
      vibrate(VIBRATION.error);
      console.error('[BarcodeScanner] Error:', err);
      
      if (err.response?.status === 503) {
        toast.error('Discogs API not configured. Please add DISCOGS_TOKEN.');
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Barcode scan limit reached. Upgrade to PRO for unlimited scans.');
      } else if (err.code === 'ERR_NETWORK') {
        toast.error('Network error. Check your connection.');
      } else {
        toast.error(`Search failed: ${err.message || 'Unknown error'}`);
      }
      setScannedBarcode(null);
      setScanLocked(false);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = async (e) => {
    e.preventDefault();
    if (!manualBarcode.trim()) return;
    setScannedBarcode(manualBarcode.trim());
    await searchByBarcode(manualBarcode.trim());
  };

  const handleScanAgain = () => {
    setScannedBarcode(null);
    setScanLocked(false);
    lastScanRef.current = null;
    setResults([]);
  };

  // Show scanned barcode state (between scan and results)
  if (scannedBarcode && loading) {
    return (
      <div className="space-y-6 py-8">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 flex items-center justify-center animate-pulse">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Barcode scanned!</h3>
            <p className="text-muted-foreground mt-1">Searching Discogs...</p>
          </div>
        </div>
        
        <div className="p-4 bg-secondary rounded-lg text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Barcode className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium">Stregkode</span>
          </div>
          <p className="font-mono text-2xl tracking-wider">{scannedBarcode}</p>
        </div>
        
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barcode Scan Counter */}
      {!isUnlimited && (
        <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Barcode className="w-4 h-4 text-primary" />
            <span className="text-sm">
              {scansRemaining > 0 ? (
                <>
                  <span className="font-medium">{scansRemaining}</span>
                  <span className="text-muted-foreground"> of {scansLimit} scans remaining</span>
                </>
              ) : (
                <span className="text-destructive font-medium">No scans remaining</span>
              )}
            </span>
          </div>
          {scansRemaining <= 10 && scansRemaining > 0 && (
            <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-xs">
              <Crown className="w-3 h-3 mr-1" />
              Upgrade
            </Badge>
          )}
        </div>
      )}
      
      {scansRemaining <= 0 && !isUnlimited ? (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 flex items-center justify-center">
            <Crown className="w-8 h-8 text-amber-500" />
          </div>
          <div>
            <h3 className="font-semibold">Scan limit reached</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upgrade to PRO for unlimited barcode scans
            </p>
          </div>
          <Button 
            onClick={() => window.location.href = '/upgrade'}
            className="bg-amber-500 hover:bg-amber-600"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to PRO
          </Button>
        </div>
      ) : results.length === 0 ? (
        <>
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'camera' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('camera')}
              className="gap-2"
              data-testid="barcode-camera-mode"
            >
              <Camera className="w-4 h-4" />
              Camera
            </Button>
            <Button
              variant={mode === 'manual' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setMode('manual'); stopCamera(); }}
              className="gap-2"
              data-testid="barcode-manual-mode"
            >
              <Keyboard className="w-4 h-4" />
              Manual
            </Button>
          </div>

          {mode === 'camera' ? (
            <div className="space-y-4">
              <div className="camera-viewfinder relative overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                
                {/* Scanning overlay with guide */}
                {scanning && (
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Corner guides */}
                    <div className="absolute inset-8">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary" />
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary" />
                    </div>
                    
                    {/* Scan line animation */}
                    <div className="absolute inset-x-12 top-1/2 -translate-y-1/2">
                      <div className="h-0.5 bg-primary/80 animate-pulse shadow-lg shadow-primary/50" />
                    </div>
                    
                    {/* Status text */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Hold camera over barcode
                    </div>
                  </div>
                )}
                
                {/* Start camera button */}
                {!scanning && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Button
                      onClick={startCamera}
                      className="gap-2 bg-primary text-primary-foreground"
                      data-testid="start-camera-btn"
                    >
                      <Camera className="w-4 h-4" />
                      Start Camera
                    </Button>
                  </div>
                )}
              </div>
              
              {scanning && (
                <Button
                  variant="outline"
                  onClick={stopCamera}
                  className="w-full gap-2"
                  data-testid="stop-camera-btn"
                >
                  <StopCircle className="w-4 h-4" />
                  Stop Camera
                </Button>
              )}

              {cameraError && (
                <p className="text-sm text-destructive">{cameraError}</p>
              )}
            </div>
          ) : (
            <form onSubmit={handleManualSearch} className="space-y-4">
              <div>
                <Label htmlFor="barcode">Stregkode (EAN/UPC)</Label>
                <Input
                  id="barcode"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  placeholder="f.eks. 5099902895529"
                  className="h-12 bg-secondary border-border font-mono"
                  data-testid="manual-barcode-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !manualBarcode.trim()}
                className="w-full gap-2 bg-primary text-primary-foreground"
                data-testid="search-barcode-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </form>
          )}
        </>
      ) : (
        <SearchResults
          results={results}
          onSelect={onSuccess}
          onBack={handleScanAgain}
        />
      )}
    </div>
  );
}
